import './Login.css';

import React, {
  useEffect,
  useState,
} from 'react';

import {
  confirmResetPassword,
  fetchAuthSession,
  resetPassword,
  signIn,
} from 'aws-amplify/auth';
import {
  Link,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // State for login
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sessionChecked, setSessionChecked] = useState(false);
  
  // State for password reset
  const [resetPasswordMode, setResetPasswordMode] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetStep, setResetStep] = useState(1); // 1: request code, 2: verify code and set new password
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  // Check if there's an existing session when component loads
  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        // Check if there's a valid session
        await fetchAuthSession();
        // If we get here, there's a valid session, redirect to dashboard
        navigate('/dashboard');
      } catch (error) {
        // No valid session, stay on login page
        console.log('No existing session:', error);
      } finally {
        setSessionChecked(true);
      }
    };

    // Check for error message in location state (from redirect)
    if (location.state?.error) {
      setError(location.state.error);
    }
    
    checkExistingSession();
  }, [navigate, location]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      console.log('Attempting to sign in with:', email);
      
      const { isSignedIn, nextStep } = await signIn({ 
        username: email, 
        password 
      });
      
      if (isSignedIn) {
        console.log('Sign in successful, redirecting to dashboard');
        navigate('/dashboard');
      } else if (nextStep) {
        // Handle additional auth challenges if needed (MFA, etc.)
        console.log('Additional auth step required:', nextStep);
        
        if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          setError('You need to change your password. Please use the forgot password function.');
        } else {
          setError(`Additional authentication step required: ${nextStep.signInStep}`);
        }
      }
    } catch (err) {
      console.error('Error signing in:', err);
      
      // Handle specific error messages more user-friendly
      if (err.name === 'UserNotFoundException') {
        setError('No account found with this email. Please check your email or sign up.');
      } else if (err.name === 'NotAuthorizedException') {
        setError('Incorrect email or password. Please try again.');
      } else if (err.name === 'UserNotConfirmedException') {
        setError('Your account is not verified. Please check your email for a verification link.');
      } else {
        setError(err.message || 'Failed to sign in');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    setResetEmail(email); // Pre-fill with current email if available
    setResetError('');
    setResetPasswordMode(true);
  };

  const handleResetRequest = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    setResetError('');
    
    try {
      console.log('Requesting password reset for:', resetEmail);
      
      await resetPassword({
        username: resetEmail
      });
      
      setResetStep(2); // Move to code verification step
    } catch (err) {
      console.error('Error requesting password reset:', err);
      
      if (err.name === 'UserNotFoundException') {
        setResetError('No account found with this email. Please check your email address.');
      } else if (err.name === 'LimitExceededException') {
        setResetError('Too many attempts. Please try again later.');
      } else {
        setResetError(err.message || 'Failed to request password reset');
      }
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetConfirm = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    setResetError('');
    
    // Validate password complexity
    if (newPassword.length < 8) {
      setResetError('Password must be at least 8 characters long');
      setResetLoading(false);
      return;
    }
    
    try {
      console.log('Confirming password reset with code');
      
      await confirmResetPassword({
        username: resetEmail,
        confirmationCode: resetCode,
        newPassword
      });
      
      // Close the dialog and show success message
      setResetPasswordMode(false);
      setError('');
      setResetStep(1);
      alert('Password has been reset successfully. Please sign in with your new password.');
    } catch (err) {
      console.error('Error confirming password reset:', err);
      
      if (err.name === 'CodeMismatchException') {
        setResetError('Invalid verification code. Please try again.');
      } else if (err.name === 'LimitExceededException') {
        setResetError('Too many attempts. Please try again later.');
      } else if (err.name === 'InvalidPasswordException') {
        setResetError('Password does not meet requirements: ' + err.message);
      } else {
        setResetError(err.message || 'Failed to reset password');
      }
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetModalClose = () => {
    setResetPasswordMode(false);
    setResetStep(1);
    setResetCode('');
    setNewPassword('');
    setResetError('');
  };

  if (!sessionChecked) {
    return (
      <Container className="login-container" maxWidth="sm">
        <div className="login-progress">
          <CircularProgress size={60} thickness={4} className="spinner" />
        </div>
      </Container>
    );
  }

  return (
    <Container className="login-container" maxWidth="sm">
      <Paper elevation={3} className="login-form">
        <Box component="form" onSubmit={handleLogin}>
          <Typography variant="h4" component="h1" gutterBottom align="center" className="form-title">
            UtilityAuditPro
          </Typography>
          
          <Typography variant="h5" component="h2" gutterBottom align="center">
            Sign In
          </Typography>
          
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
          
          <div className="form-field">
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              margin="normal"
              required
              autoFocus
            />
          </div>
          
          <div className="form-field">
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              margin="normal"
              required
            />
          </div>
          
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            className="login-button"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} className="spinner" /> : 'Sign In'}
          </Button>
          
          <div className="form-actions">
            <Button 
              variant="text" 
              onClick={handleForgotPassword}
              disabled={loading}
              className="form-link"
            >
              Forgot Password?
            </Button>
            
            <Link to="/signup" className="form-link">
              <Button variant="text" color="primary" disabled={loading}>
                Sign Up
              </Button>
            </Link>
          </div>
        </Box>
      </Paper>

      {/* Password Reset Dialog */}
      <Dialog open={resetPasswordMode} onClose={handleResetModalClose}>
        <DialogTitle>
          {resetStep === 1 ? 'Reset Your Password' : 'Verify Code'}
        </DialogTitle>
        
        <DialogContent>
          {resetError && (
            <div className="error-message">
              {resetError}
            </div>
          )}
          
          {resetStep === 1 ? (
            <>
              <DialogContentText>
                Enter your email address and we'll send you a code to reset your password.
              </DialogContentText>
              
              <div className="form-field">
                <TextField
                  autoFocus
                  margin="dense"
                  label="Email Address"
                  type="email"
                  fullWidth
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                />
              </div>
            </>
          ) : (
            <>
              <DialogContentText>
                Enter the confirmation code sent to {resetEmail} and your new password.
              </DialogContentText>
              
              <div className="form-field">
                <TextField
                  autoFocus
                  margin="dense"
                  label="Confirmation Code"
                  fullWidth
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  required
                />
              </div>
              
              <div className="form-field">
                <TextField
                  margin="dense"
                  label="New Password"
                  type="password"
                  fullWidth
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  helperText="Minimum 8 characters, include uppercase, lowercase, number, and special character"
                />
              </div>
            </>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleResetModalClose}>
            Cancel
          </Button>
          
          {resetStep === 1 ? (
            <Button 
              onClick={handleResetRequest} 
              variant="contained" 
              color="primary"
              className="login-button"
              disabled={resetLoading}
            >
              {resetLoading ? <CircularProgress size={24} className="spinner" /> : 'Send Code'}
            </Button>
          ) : (
            <Button 
              onClick={handleResetConfirm} 
              variant="contained" 
              color="primary"
              className="login-button"
              disabled={resetLoading}
            >
              {resetLoading ? <CircularProgress size={24} className="spinner" /> : 'Reset Password'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Login;